import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { Layout } from 'antd';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import i18n from './i18n';

import Header from './components/Header';
import Footer from './components/Footer';
import TermsModal from './components/TermsModal';
import ErrorBoundary from './components/ErrorBoundary';
import Legal from './pages/Legal';
import Home from './pages/Home';
import Login from './pages/Login';

// 部署后旧标签页里的页面拆包文件名已变（旧文件不存在）→ 加载失败。自动整页刷新一次拿新版本；
// 刷新后仍失败（真断网等）才抛给错误边界，避免死循环。
const lazyPage = (load) => lazy(() => load().then(
  (mod) => { try { sessionStorage.removeItem('chunk_reload'); } catch (e) { /* 无痕模式 */ } return mod; },
  (err) => {
    let reloaded = false;
    try { reloaded = sessionStorage.getItem('chunk_reload') === '1'; sessionStorage.setItem('chunk_reload', '1'); } catch (e) { reloaded = true; }
    if (!reloaded) { window.location.reload(); return new Promise(() => {}); }
    throw err;
  }
));

// 首页 / 登录 / 协议页直接打包；其余页面按路由拆包，首屏不必下载后台、钱包等用不到的代码
const Register = lazyPage(() => import('./pages/Register'));
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const Trips = lazyPage(() => import('./pages/Trips'));
const Requests = lazyPage(() => import('./pages/Requests'));
const Orders = lazyPage(() => import('./pages/Orders'));
const Wallet = lazyPage(() => import('./pages/Wallet'));
const Messages = lazyPage(() => import('./pages/Messages'));
const Disputes = lazyPage(() => import('./pages/Disputes'));
const Profile = lazyPage(() => import('./pages/Profile'));
const UserProfile = lazyPage(() => import('./pages/UserProfile'));
const TripDetail = lazyPage(() => import('./pages/TripDetail'));
const RequestDetail = lazyPage(() => import('./pages/RequestDetail'));
const OrderDetail = lazyPage(() => import('./pages/OrderDetail'));
const PublishTrip = lazyPage(() => import('./pages/PublishTrip'));
const PublishRequest = lazyPage(() => import('./pages/PublishRequest'));
const Verification = lazyPage(() => import('./pages/Verification'));
const ForgotPassword = lazyPage(() => import('./pages/ForgotPassword'));
const AdminLogin = lazyPage(() => import('./pages/AdminLogin'));
const AdminDashboard = lazyPage(() => import('./pages/AdminDashboard'));
const Settings = lazyPage(() => import('./pages/Settings'));
const MyListings = lazyPage(() => import('./pages/MyListings'));

const { Content } = Layout;

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading-container">{i18n.t('common:loading')}</div>;
  }

  // 带上来源页：登录后回到原页面（Login 读 location.state.from）
  return user ? children : <Navigate to="/login" replace state={{ from: location }} />;
};

// 换页回到顶部：SPA 换路由不会自动滚动，发布后跳详情 / 注册后进首页会停在上一页的底部。
// 后退 / 前进（POP）不处理，交给浏览器恢复原滚动位置
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== 'POP') window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
};

// 换页即重置错误边界，出错后点导航能进入别的页
const PageErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

// 公开路由组件（已登录用户重定向到首页）
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-container">{i18n.t('common:loading')}</div>;
  }
  
  return user ? <Navigate to="/" replace /> : children;
};

function App() {
  return (
    <AuthProvider>
      <Layout className="layout">
        <ScrollToTop />
        <Header />
        <Content className="ant-layout-content">
          <PageErrorBoundary>
          <Suspense fallback={<div className="loading-container">{i18n.t('common:loading')}</div>}>
          <Routes>
            {/* 公开路由 */}
            <Route path="/" element={<Home />} />
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            
            {/* 受保护的路由 */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            {/* 行程 / 需求的列表与详情、用户信誉页可匿名浏览（后端接口本就公开，与移动端一致）；
                下单、联系等操作在页面内要求登录 */}
            <Route path="/trips" element={<Trips />} />
            <Route 
              path="/trips/create" 
              element={
                <ProtectedRoute>
                  <PublishTrip />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trips/publish" 
              element={
                <ProtectedRoute>
                  <PublishTrip />
                </ProtectedRoute>
              } 
            />
            <Route path="/trips/:id" element={<TripDetail />} />
            <Route path="/requests" element={<Requests />} />
            <Route 
              path="/requests/create" 
              element={
                <ProtectedRoute>
                  <PublishRequest />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/requests/publish" 
              element={
                <ProtectedRoute>
                  <PublishRequest />
                </ProtectedRoute>
              } 
            />
            <Route path="/requests/:id" element={<RequestDetail />} />
            <Route
              path="/my/listings"
              element={
                <ProtectedRoute>
                  <MyListings />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/orders" 
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute>
                  <OrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <Wallet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/disputes"
              element={
                <ProtectedRoute>
                  <Disputes />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/verification"
              element={
                <ProtectedRoute>
                  <Verification />
                </ProtectedRoute>
              }
            />
            <Route path="/users/:userId" element={<UserProfile />} />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            
            {/* 公开路由 */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* 404路由 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </PageErrorBoundary>
        </Content>
        <Footer />
        <TermsModal />
        <Toaster position="top-right" />
      </Layout>
    </AuthProvider>
  );
}

export default App;
